// @ts-strict-ignore
import React, {
  useState, useCallback, useEffect,
} from 'react';
import {
  selectIsPendingHistoryCommit,
  selectPreviewPrecedenceIndexedAction,
} from 'flow/store/redux/selectors';
import { Button, ButtonVariant, IconName } from '@usealloy/component-library';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import IslandActionRuleEditor from '../../IslandActionRuleEditor';
import type { BaseWorkflowRoutingIslandProps } from '../types';
import { ActionType } from '~/dashboard/views/workflows/store/engine/constants';
import BaseComplexIsland from '../../BaseComplexIsland';
import type { Action } from '~/dashboard/views/workflows/store/engine/types';
import { resetNodeAndConnectorState } from '~/dashboard/views/workflows/store/redux/slice';
import {
  commitWorkflowHistoryTransaction,
  cancelMultiStepActionPreviewThunk,
} from 'flow/store/redux/thunks';
import { UserContext } from '~/dashboard/context';
import { Operation } from 'flow/service/changelog/types';
import { createUiEffectContext } from 'flow/service/history';
import { changeDescription } from 'flow/store/redux/types';

import {
  handleOnAddWorkflowRoutingClick as createWorkflowRoutingPreview,
} from './helpers';


interface ComplexWorkflowRoutingIslandProps extends BaseWorkflowRoutingIslandProps { }

export default function ComplexWorkflowRoutingIsland({
  workflowRoutingActions,
}: ComplexWorkflowRoutingIslandProps) {
  const dispatch = useAppDispatch();
  const { featureFlags } = React.useContext(UserContext);
  const previewPrecedenceIndexedAction = useAppSelector(
    selectPreviewPrecedenceIndexedAction,
  );
  const previewWorkflowRoutingAction = previewPrecedenceIndexedAction?.type === ActionType.WORKFLOW
    ? previewPrecedenceIndexedAction
    : undefined;
  const reduxPreviewWorkflowRoutingPublicId = previewWorkflowRoutingAction?.publicId;
  const isPendingHistoryCommit = useAppSelector(selectIsPendingHistoryCommit);

  const [
    previewWorkflowRoutingActionPublicId, setPreviewWorkflowRoutingActionPublicId,
  ] = useState<Action['publicId']>(previewWorkflowRoutingAction?.publicId ?? null);
  const isAddingNewWorkflowRoutingAction = Boolean(previewWorkflowRoutingActionPublicId);
  const isTrackedPreviewActionValid = !previewWorkflowRoutingActionPublicId
    || workflowRoutingActions.some(
      ({ publicId }) => publicId === previewWorkflowRoutingActionPublicId,
    );
  useEffect(() => {
    if (!isTrackedPreviewActionValid) {
      setPreviewWorkflowRoutingActionPublicId(null);
    } else if (reduxPreviewWorkflowRoutingPublicId) {
      setPreviewWorkflowRoutingActionPublicId(reduxPreviewWorkflowRoutingPublicId);
    }
  }, [
    isTrackedPreviewActionValid,
    reduxPreviewWorkflowRoutingPublicId,
  ]);

  const cancelPreview = useCallback(() => {
    if (previewWorkflowRoutingActionPublicId) {
      dispatch(cancelMultiStepActionPreviewThunk(
        previewWorkflowRoutingActionPublicId,
      ));
      setPreviewWorkflowRoutingActionPublicId(null);
    }
  }, [dispatch, previewWorkflowRoutingActionPublicId]);

  const handleDrawerToggle = useCallback(() => {
    cancelPreview();
    dispatch(resetNodeAndConnectorState());
  }, [cancelPreview, dispatch]);


  const handleOnAddWorkflowRoutingClick = useCallback(() => {
    const publicId = createWorkflowRoutingPreview(
      dispatch,
      workflowRoutingActions,
      Boolean(featureFlags?.workflowEditorUndoRedo),
    );
    if (!publicId) return;

    setPreviewWorkflowRoutingActionPublicId(publicId);
  }, [dispatch, featureFlags?.workflowEditorUndoRedo, workflowRoutingActions]);

  const handleOnSubmitAction = useCallback(() => {
    if (previewWorkflowRoutingActionPublicId) {
      dispatch(commitWorkflowHistoryTransaction({
        context: {
          operation: Operation.Add,
          uiEffectContext: createUiEffectContext(changeDescription.action, {
            publicId: previewWorkflowRoutingActionPublicId,
          }),
        },
      }));
    }

    setPreviewWorkflowRoutingActionPublicId(null);
  }, [dispatch, previewWorkflowRoutingActionPublicId]);

  const handleOnCancelAction = cancelPreview;

  return (
    <BaseComplexIsland
      onDrawerToggle={handleDrawerToggle}
      title="Workflow Routing"
    >
      <Button
        disabled={isAddingNewWorkflowRoutingAction}
        icon={IconName.Add}
        onClick={handleOnAddWorkflowRoutingClick}
        variant={ButtonVariant.tertiary}
      >
        Add Workflow
      </Button>

      {workflowRoutingActions.map(workflowRoutingAction => (
        <IslandActionRuleEditor
          key={workflowRoutingAction.publicId}
          action={workflowRoutingAction}
          actionType={ActionType.WORKFLOW}
          allActionsOfActionType={workflowRoutingActions}
          isPreviewAction={workflowRoutingAction.publicId === previewWorkflowRoutingActionPublicId}
          isInteractionDisabled={Boolean(
            isPendingHistoryCommit
            && reduxPreviewWorkflowRoutingPublicId !== workflowRoutingAction.publicId,
          )}
          onCancelAction={handleOnCancelAction}
          onSubmitAction={handleOnSubmitAction}
        />
      ))}


    </BaseComplexIsland>


  );
}
